// Cache for the sidebar's "Active Courses / Pending Tasks / Completed" counters.
//
// Those three numbers cost three requests -- dashboard/stats, assignments and
// quizzes/student/assignments -- and the sidebar mounts on every page, so a
// hard load of any page paid for them even when nothing on that page used the
// data. They also rendered 0/0/0 until the fetch landed and then jumped to the
// real values.
//
// They are now counted server-side by /api/dashboard/task-counters, so the
// cost is one small response rather than three payloads. This cache still
// earns its keep: it paints the last known numbers immediately instead of
// 0/0/0, and skips even that one request while they are fresh.
//
// sessionStorage, not localStorage: these are per-tab and should not outlive
// the tab, and a signed-out user shouldn't leave counts behind on the device.

const KEY = 'faithinsight_sidebar_counters';

// How long cached counters are served without refetching. Short enough that a
// submission is reflected quickly on its own, and the mutation hook below
// closes the gap for the cases that actually change the numbers.
export const COUNTERS_TTL_MS = 60000;

// Anything that changes a student's pending/completed task counts dispatches
// this, so the sidebar can drop the cache and refresh rather than waiting out
// the TTL.
export const TASKS_CHANGED_EVENT = 'faithinsight:tasks-changed';

export const EMPTY_COUNTERS = { courses: 0, pending: 0, completed: 0 };

/** Cached counters, or null when absent/expired/unreadable. */
export function readCachedCounters(ttlMs = COUNTERS_TTL_MS) {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.at !== 'number') return null;
    if (Date.now() - parsed.at > ttlMs) return null;
    const { courses, pending, completed } = parsed;
    if ([courses, pending, completed].some((n) => typeof n !== 'number')) return null;
    return { courses, pending, completed };
  } catch (e) {
    // Private mode / blocked storage: behave as a cache miss.
    return null;
  }
}

/**
 * Cached counters regardless of age. Used for the first paint so the sidebar
 * shows the last known numbers instead of 0/0/0 while a refresh is in flight.
 */
export function readStaleCounters() {
  return readCachedCounters(Infinity);
}

export function writeCachedCounters({ courses, pending, completed }) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ courses, pending, completed, at: Date.now() }));
  } catch (e) {
    // Nothing to do -- we just refetch next load.
  }
}

export function clearCachedCounters() {
  try {
    sessionStorage.removeItem(KEY);
  } catch (e) {
    // Ignore.
  }
}

/** Call after anything that changes pending/completed task counts. */
export function notifyTasksChanged() {
  clearCachedCounters();
  try {
    window.dispatchEvent(new CustomEvent(TASKS_CHANGED_EVENT));
  } catch (e) {
    // Ignore.
  }
}
